/**
 * Pure policy + formatting for message pop-ups (the "кто пишет + превью" card for a
 * DM, or "вас упомянули" for a server channel) and where they go.
 *
 * Everything store-/HeroUI-bound lives in `dmToastQueue.tsx`; this module is
 * deliberately side-effect-free so the gating + truncation rules are unit-test
 * covered (mandatory coverage area: client mutes/notifications).
 */
import type { NotificationLevel } from '@/lib/muteSettings';

/** Max preview length before we ellipsize. Long enough to read the gist, short
 *  enough to stay one or two lines in a 360px card. */
export const DM_PREVIEW_MAX = 90;

/** Collapse whitespace + clip to `max` graphemes-ish (code units are fine here —
 *  previews are short chat text, not arbitrary unicode art). Adds an ellipsis
 *  only when something was actually cut. */
export function truncatePreview(text: string, max: number = DM_PREVIEW_MAX): string {
  const normalized = text.replace(/\s+/g, ' ').trim();
  if (normalized.length <= max) return normalized;
  return normalized.slice(0, max - 1).trimEnd() + '…';
}

/**
 * Where a pop-up for an incoming message goes — one surface, never two (owner's decision
 * 21.10.2026: everything about notifications in one place):
 *   - `in-app`  — Bunker is the surface you are looking at: its own card;
 *   - `desktop` — Bunker is hidden in Desktop: the shell's card over everything (desktop/notifyOverlay.js);
 *   - `shell`   — Bunker is the platform's Chat frame and the shell does not show it: the shell pops it up;
 *   - `none`    — nothing pops up. A plain browser in the background still gets the OS banner
 *                 from lib/notifications.ts when the person turned it on.
 * «Не беспокоить» silences every pop-up; the unread counters still count.
 * The Cispracs ID account's `popup` for the topic (dm / mention) is a ceiling on
 * all three surfaces: off there, nothing pops up anywhere (lib/accountNotifyPrefs.ts).
 */
export type CardSurface = 'in-app' | 'desktop' | 'shell' | 'none';

export function messageCardSurface(input: {
  /** The message is mine (echo of my own send). */
  isOwnMessage: boolean;
  /** Effective mute level for the channel (a DM has no server fallback). */
  level: NotificationLevel;
  /** My status is «Не беспокоить». */
  doNotDisturb: boolean;
  /** The account allows a pop-up for this topic (true with no account restriction). */
  accountPopup: boolean;
  /** Bunker is the surface you are looking at (lib/desktopBridge.ts isBunkerOnScreen). */
  bunkerOnScreen: boolean;
  /** Bunker has exactly this channel open. It counts only while Bunker is on screen. */
  viewingThisChannel: boolean;
  /** The Desktop shell can draw its own card. */
  desktopCard: boolean;
  /** Bunker is the platform's Chat frame. */
  platformFrame: boolean;
}): CardSurface {
  if (input.isOwnMessage || input.doNotDisturb || !input.accountPopup || input.level === 'NOTHING') return 'none';
  if (input.bunkerOnScreen) return input.viewingThisChannel ? 'none' : 'in-app';
  if (input.desktopCard) return 'desktop';
  if (input.platformFrame) return 'shell';
  return 'none';
}

/** Coalescing counter: a 2nd+ message from the same chat (while its card is
 *  still up) bumps the count instead of stacking another card. */
export function nextDmToastCount(prevCount: number | undefined): number {
  return (prevCount ?? 0) + 1;
}
